import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Banknote, Plus, Trash2, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { banksApi } from '@/services/endpoints/masterData';
import { queryKeys } from '@/services/queryKeys';
import { localKey } from '@/lib/utils';
import { today } from '@/lib/dateUtils';
import type { CollectionIssue } from '@/domain/collectionEngine';
import type { PaymentDraft } from '@/domain/types';

/**
 * The "money handed over" section: any number of cash amounts and cheques.
 *
 * A cheque's three mandatory fields are enforced inline, because an agent
 * standing in a shop should discover a missing cheque number then — not after
 * pressing Issue.
 */
export function PaymentRows({
  payments,
  onChange,
  issues,
}: {
  payments: PaymentDraft[];
  onChange: (payments: PaymentDraft[]) => void;
  issues: CollectionIssue[];
}): JSX.Element {
  const { t } = useTranslation();

  const { data: banks } = useQuery({
    queryKey: queryKeys.banks.list(false),
    queryFn: () => banksApi.list(false),
    // The bank list barely changes; refetching it per visit is wasted work.
    staleTime: 30 * 60 * 1000,
  });

  const addPayment = (method: 'CASH' | 'CHEQUE') =>
    onChange([
      ...payments,
      {
        key: localKey(),
        method,
        amountCents: 0,
        chequeNo: '',
        chequeDate: method === 'CHEQUE' ? today() : '',
        bankId: null,
      },
    ]);

  const updatePayment = (key: string, patch: Partial<PaymentDraft>) =>
    onChange(payments.map((payment) => (payment.key === key ? { ...payment, ...patch } : payment)));

  const removePayment = (key: string) =>
    onChange(payments.filter((payment) => payment.key !== key));

  const issueFor = (key: string) => issues.find((issue) => issue.paymentKey === key);

  return (
    <div className="space-y-3">
      {payments.length === 0 && (
        <p className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">
          {t('collection.issues.NO_PAYMENTS')}
        </p>
      )}

      {payments.map((payment) => {
        const issue = issueFor(payment.key);

        return (
          <div
            key={payment.key}
            className="rounded-lg border border-slate-200 bg-slate-50/60 p-3"
          >
            <div className="flex items-start gap-3">
              <div className="mt-2 shrink-0 rounded-lg bg-white p-2 text-slate-500 shadow-sm">
                {payment.method === 'CASH' ? (
                  <Wallet className="h-4 w-4" />
                ) : (
                  <Banknote className="h-4 w-4" />
                )}
              </div>

              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                <MoneyInput
                  label={
                    payment.method === 'CASH' ? t('collection.cash') : t('collection.cheque')
                  }
                  value={payment.amountCents}
                  onChange={(cents) => updatePayment(payment.key, { amountCents: cents })}
                  required
                />

                {payment.method === 'CHEQUE' && (
                  <>
                    <Input
                      label={t('collection.chequeNo')}
                      value={payment.chequeNo}
                      onChange={(event) =>
                        updatePayment(payment.key, { chequeNo: event.target.value })
                      }
                      required
                    />
                    <Input
                      label={t('collection.chequeDate')}
                      type="date"
                      value={payment.chequeDate}
                      onChange={(event) =>
                        updatePayment(payment.key, { chequeDate: event.target.value })
                      }
                      // Post-dating is normal here, so no max is imposed.
                      required
                    />
                    <Select
                      label={t('collection.bank')}
                      value={payment.bankId ?? ''}
                      onChange={(event) =>
                        updatePayment(payment.key, {
                          bankId: event.target.value ? Number(event.target.value) : null,
                        })
                      }
                      placeholder={t('common.selectPlaceholder')}
                      options={(banks ?? []).map((bank) => ({
                        value: bank.id,
                        label: `${bank.shortCode} — ${bank.name}`,
                      }))}
                      required
                    />
                  </>
                )}
              </div>

              <Button
                variant="ghost"
                size="sm"
                className="mt-6 shrink-0"
                onClick={() => removePayment(payment.key)}
                aria-label={t('common.remove')}
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>

            {issue && (
              <p className="mt-2 text-xs text-red-600" role="alert">
                {t(`collection.issues.${issue.code}` as never, issue.values)}
              </p>
            )}
          </div>
        );
      })}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => addPayment('CASH')}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {t('collection.addCash')}
        </Button>
        <Button
          variant="outline"
          onClick={() => addPayment('CHEQUE')}
          leftIcon={<Plus className="h-4 w-4" />}
        >
          {t('collection.addCheque')}
        </Button>
      </div>
    </div>
  );
}
